import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader";
import { useToast } from "../../../hooks/useToast";
import { formatR } from "../../../utils/money";
import {
  allocateAvailableMemorialPlot,
  allocateMemorialPlot,
  approveMemorialAppointment,
  approveMemorialCertificate,
  approveMemorialFinance,
  createMemorialApplication,
  createMemorialEnquiry,
  createMemorialLocation,
  createMemorialPlot,
  generateMemorialCertificate,
  generateMemorialInvoice,
  getMemorialAppointments,
  getMemorialApplication,
  getMemorialApplications,
  getMemorialDashboard,
  getMemorialEnquiries,
  getFilteredMemorialReports,
  getMemorialLocations,
  getMemorialPlots,
  issueMemorialCertificate,
  recordMemorialBurialVerification,
  rejectMemorialAppointment,
  rejectMemorialFinance,
  requestMemorialAppointment,
  requestMemorialPaymentInfo,
  returnMemorialFinance,
  returnMemorialVerification,
  saveMemorialTimeSlot,
  submitMemorialApplication,
  uploadMemorialDocument,
  uploadMemorialProofOfPayment,
  verifyMemorialApplication,
} from "../../../services/dashboard";
import {
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsMetricCard,
  OperationsPageShell,
  OperationsSectionHeader,
  formatOperationsStatus,
  getOperationsStatusSx,
  ifasColors,
  operationsActionBarSx,
  operationsDetailPaperSx,
} from "../ifas/components/OperationsUI";

type ModalMode =
  | ""
  | "enquiry"
  | "appointment"
  | "application"
  | "location"
  | "plot"
  | "timeSlot"
  | "document"
  | "payment"
  | "certificate"
  | "burial"
  | "action";

const emptyEnquiry = {
  clientSource: "ONLINE",
  applicantName: "",
  contactNumber: "",
  emailAddress: "",
  deceasedInformation: "",
  preferredDate: "",
  preferredTime: "",
  preferredLocationId: "",
  requestType: "CONSULTATION",
  additionalNotes: "",
};

const emptyAppointment = {
  enquiryId: "",
  locationId: "",
  appointmentType: "CONSULTATION",
  appointmentDate: "",
  appointmentTime: "09:00",
  notes: "",
};

const emptyApplication = {
  enquiryId: "",
  appointmentId: "",
  applicantName: "",
  applicantContactNumber: "",
  applicantEmail: "",
  applicantIdNumber: "",
  deceasedName: "",
  deceasedIdNumber: "",
  deceasedDateOfDeath: "",
  selectedPlotId: "",
  memorialPackage: "Single Plot",
  branchLocation: "",
  declarationAccepted: true,
  consentAccepted: true,
  consultationNotes: "",
};

const emptyLocation = {
  name: "",
  description: "",
  siteViewUrl: "",
  active: true,
};

const emptyPlot = {
  locationId: "",
  plotNumber: "",
  price: "30000",
  maintenanceFee: "25000",
  maintenanceTermYears: "10",
};

const emptyTimeSlot = {
  locationId: "",
  dayOfWeek: "MONDAY",
  slotTime: "09:00",
  active: true,
};

const emptyDocument = {
  documentType: "APPLICANT_ID",
  fileName: "",
  fileUrl: "",
  certifiedCopy: true,
  uploadedBy: "",
};

const emptyPayment = {
  amount: "",
  paymentReference: "",
  proofOfPaymentUrl: "",
  actionedBy: "",
  comments: "",
};

const emptyAction = {
  actionedBy: "",
  comments: "",
  alternativeDate: "",
  alternativeTime: "",
  certificateUrl: "",
  signatureTemplateUrl: "",
  gccoSignedBy: "",
  clientSigned: true,
  deliveryMethod: "CLIENT_PORTAL_DOWNLOAD",
  downloaded: false,
  collected: false,
};

const emptyBurial = {
  burialOrderOriginalVerified: true,
  deceasedIdOriginalVerified: true,
  applicantIdOriginalVerified: true,
  verifiedBy: "",
  comments: "",
};

const emptyReportFilters = {
  fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
  toDate: dayjs().format("YYYY-MM-DD"),
  branchLocation: "",
  locationId: "",
  applicationStatus: "",
  financeStatus: "",
  certificateStatus: "",
  plotStatus: "",
};

const applicationStatusOptions = [
  "DRAFT",
  "APPOINTMENT_REQUESTED",
  "APPOINTMENT_CONFIRMED",
  "APPOINTMENT_REJECTED",
  "CONSULTATION_COMPLETED",
  "APPLICATION_SUBMITTED",
  "PENDING_VERIFICATION",
  "PENDING_FINANCE_APPROVAL",
  "FINANCE_REJECTED",
  "PENDING_CERTIFICATE_APPROVAL",
  "CERTIFICATE_GENERATED",
  "CERTIFICATE_ISSUED",
  "CLOSED",
];

const financeStatusOptions = ["NOT_REQUIRED", "PENDING", "APPROVED", "REJECTED", "RETURNED_FOR_CORRECTION"];
const certificateStatusOptions = ["DRAFT", "GENERATED", "PENDING_GCCO_SIGNATURE", "APPROVED", "ISSUED"];
const plotStatusOptions = ["AVAILABLE", "RESERVED", "ALLOCATED", "UNAVAILABLE"];

const getItems = (response: any) => response?.data?.items || [];
const display = (value: any) => value || "Not captured";
const toDate = (value?: string) => (value ? dayjs(value).format("YYYY-MM-DD") : "Not set");
const toTime = (value?: string) => (value ? String(value).slice(0, 5) : "Not set");
const toTimeValue = (value?: string) => (value ? String(value).slice(0, 5) : "");

const workflowSteps = [
  "DRAFT",
  "APPOINTMENT_REQUESTED",
  "APPOINTMENT_CONFIRMED",
  "CONSULTATION_COMPLETED",
  "PENDING_VERIFICATION",
  "PENDING_FINANCE_APPROVAL",
  "PENDING_CERTIFICATE_APPROVAL",
  "CERTIFICATE_GENERATED",
  "CERTIFICATE_ISSUED",
  "CLOSED",
];

const requirementCards = [
  {
    title: "Client Intake",
    text: "Online and walk-in enquiries, appointments, consultation notes, and application capture.",
  },
  {
    title: "Plot Control",
    text: "Location-based inventory, availability counts, manual allocation, and first-available auto allocation.",
  },
  {
    title: "Finance Gate",
    text: "Invoice, POP upload, finance review, approval, rejection, and payment clearance status.",
  },
  {
    title: "Ownership Pack",
    text: "Certificate generation, GCCO signing, issuing channel, download or collection tracking, and burial originals.",
  },
];

const memorialPrimaryButtonSx = {
  borderRadius: "10px",
  px: 1.55,
  minHeight: 36,
  textTransform: "none",
  fontWeight: 950,
  bgcolor: ifasColors.orange,
  color: "#FFFFFF",
  boxShadow: "0 10px 18px rgba(234, 104, 0, 0.18)",
  "&:hover": {
    bgcolor: "#C95A00",
  },
};

const memorialOutlineButtonSx = {
  borderRadius: "10px",
  px: 1.55,
  minHeight: 36,
  textTransform: "none",
  fontWeight: 950,
  borderColor: alpha(ifasColors.orange, 0.55),
  bgcolor: "#FFFFFF",
  color: ifasColors.orange,
  "&:hover": {
    borderColor: ifasColors.orange,
    bgcolor: alpha(ifasColors.orange, 0.1),
  },
};

// Airy, mock-matching table styling: white uppercase letter-spaced
// headers, tall spacious rows, no inner border (the panel card owns it).
const memorialTableInnerSx = {
  bgcolor: "transparent",
  boxShadow: "none",
  borderRadius: 0,
  "& .MuiTableCell-root": {
    color: ifasColors.text,
    fontSize: 14,
    borderColor: ifasColors.border,
  },
  "& .MuiTableCell-root:first-of-type": { pl: 2.5 },
  "& .MuiTableCell-root:last-of-type": { pr: 2.5 },
  "& .MuiIconButton-root": { width: 34, height: 34, color: ifasColors.muted },
  "& .MuiIconButton-root:hover": { bgcolor: ifasColors.softOrange, color: ifasColors.orange },
};

const memorialTableHeadSx = {
  "& .MuiTableCell-root": {
    bgcolor: "#FFFFFF",
    color: ifasColors.muted,
    fontWeight: 800,
    fontSize: 11,
    letterSpacing: "0.7px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${ifasColors.border}`,
    py: 1.5,
  },
};

const memorialRowSx = {
  cursor: "pointer",
  transition: "background-color 160ms ease",
  "& .MuiTableCell-root": {
    py: 2,
    borderBottom: `1px solid ${ifasColors.border}`,
    verticalAlign: "middle",
  },
  "&:last-of-type .MuiTableCell-root": { borderBottom: "none" },
  "&:hover": { bgcolor: "#FFF9F4" },
};

function filterRows<T>(rows: T[], term: string, pick: (row: T) => Array<unknown>): T[] {
  const needle = (term || "").trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    pick(row).some((value) => String(value ?? "").toLowerCase().includes(needle)),
  );
}

function MemorialTablePanel({
  title,
  count,
  searchValue,
  onSearch,
  actions,
  children,
}: {
  title: string;
  count: number;
  searchValue: string;
  onSearch: (value: string) => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ifasColors.border}`,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        boxShadow: "0 10px 26px rgba(20, 24, 31, 0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          borderBottom: `1px solid ${ifasColors.border}`,
        }}
      >
        <Typography fontWeight={900} fontSize={20} color={ifasColors.text}>
          {title}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            value={searchValue}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: ifasColors.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#F8FAFB",
                "& fieldset": { borderColor: ifasColors.border },
              },
            }}
          />
          <Box
            sx={{
              minWidth: 34,
              height: 34,
              px: 1,
              borderRadius: "10px",
              bgcolor: alpha(ifasColors.orange, 0.16),
              color: ifasColors.orange,
              fontSize: 14,
              fontWeight: 900,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {count}
          </Box>
          {actions}
        </Box>
      </Box>
      <Box sx={{ overflowX: "auto" }}>{children}</Box>
    </Paper>
  );
}

export default function MemorialsPortal() {
  const { showInfoToast } = useToast();
  const [tab, setTab] = useState("applications");
  const [showCoverage, setShowCoverage] = useState(true);
  const [search, setSearch] = useState<Record<string, string>>({});
  const q = (key: string) => search[key] || "";
  const setQ = (key: string) => (value: string) => setSearch((prev) => ({ ...prev, [key]: value }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<any>({});
  const [reports, setReports] = useState<any>({});
  const [locations, setLocations] = useState<any[]>([]);
  const [plots, setPlots] = useState<any[]>([]);
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [modalMode, setModalMode] = useState<ModalMode>("");
  const [actionType, setActionType] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [reportFilters, setReportFilters] = useState<any>(emptyReportFilters);

  const availablePlots = useMemo(
    () => plots.filter((plot) => plot.status === "AVAILABLE"),
    [plots],
  );

  const selectedApplicationId = selectedApplication?.application?.id || selectedApplication?.id;

  const loadAll = async () => {
    setLoading(true);
    const [dashboardRes, reportsRes, locationRes, plotRes, enquiryRes, applicationRes, appointmentRes] =
      await Promise.all([
        getMemorialDashboard(),
        getFilteredMemorialReports(cleanPayload(reportFilters)),
        getMemorialLocations(),
        getMemorialPlots(),
        getMemorialEnquiries({ page: 0, size: 50 }),
        getMemorialApplications({ page: 0, size: 50 }),
        getMemorialAppointments({
          fromDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
          toDate: dayjs().add(60, "day").format("YYYY-MM-DD"),
          page: 0,
          size: 50,
        }),
      ]);

    if (dashboardRes?.status === 200) setDashboard(dashboardRes.data || {});
    if (reportsRes?.status === 200) setReports(reportsRes.data || {});
    if (locationRes?.status === 200) setLocations(getItems(locationRes));
    if (plotRes?.status === 200) setPlots(getItems(plotRes));
    if (enquiryRes?.status === 200) setEnquiries(getItems(enquiryRes));
    if (applicationRes?.status === 200) setApplications(getItems(applicationRes));
    if (appointmentRes?.status === 200) setAppointments(getItems(appointmentRes));

    const failed = [dashboardRes, reportsRes, locationRes, plotRes, enquiryRes, applicationRes, appointmentRes].find(
      (response) => response?.status && response.status >= 400,
    );
    if (failed) showInfoToast(failed?.data?.message || "Failed to load memorial information");
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openModal = (mode: ModalMode, initialForm: any = {}) => {
    setModalMode(mode);
    setForm(initialForm);
  };

  const closeModal = () => {
    setModalMode("");
    setActionType("");
    setSelectedAppointment(null);
    setForm({});
  };

  const reloadSelectedApplication = async (applicationId: string) => {
    const response = await getMemorialApplication(applicationId);
    if (response?.status === 200) setSelectedApplication(response.data);
  };

  const loadReports = async (filters = reportFilters) => {
    setSaving(true);
    const response = await getFilteredMemorialReports(cleanPayload(filters));
    if (response?.status === 200) {
      setReports(response.data || {});
      showInfoToast("Report filters applied");
    } else {
      showInfoToast(response?.data?.message || "Failed to load memorial reports");
    }
    setSaving(false);
  };

  const resetReports = async () => {
    setReportFilters(emptyReportFilters);
    await loadReports(emptyReportFilters);
  };

  const saveAndRefresh = async (request: Promise<any>, successMessage: string, applicationId?: string) => {
    setSaving(true);
    const response = await request;
    if (response?.status === 200) {
      showInfoToast(successMessage);
      closeModal();
      await loadAll();
      if (applicationId) await reloadSelectedApplication(applicationId);
    } else {
      showInfoToast(response?.data?.message || "Memorial action failed");
    }
    setSaving(false);
  };

  const submitModal = async () => {
    if (modalMode === "enquiry") return saveAndRefresh(createMemorialEnquiry(cleanPayload(form)), "Enquiry captured");
    if (modalMode === "appointment") return saveAndRefresh(requestMemorialAppointment(cleanPayload(form), form.bookedBy), "Appointment requested");
    if (modalMode === "application") return saveAndRefresh(createMemorialApplication(cleanPayload(form)), "Application created");
    if (modalMode === "location") return saveAndRefresh(createMemorialLocation(cleanPayload(form)), "Location saved");
    if (modalMode === "plot") return saveAndRefresh(createMemorialPlot(cleanPayload(form)), "Plot created");
    if (modalMode === "timeSlot") return saveAndRefresh(saveMemorialTimeSlot(cleanPayload(form)), "Time slot saved");
    if (modalMode === "document" && selectedApplicationId) {
      return saveAndRefresh(uploadMemorialDocument(selectedApplicationId, cleanPayload(form)), "Document attached", selectedApplicationId);
    }
    if (modalMode === "payment" && selectedApplicationId) {
      const request =
        actionType === "invoice"
          ? generateMemorialInvoice(selectedApplicationId, cleanPayload(form))
          : uploadMemorialProofOfPayment(selectedApplicationId, cleanPayload(form));
      return saveAndRefresh(request, actionType === "invoice" ? "Invoice generated" : "Proof of payment uploaded", selectedApplicationId);
    }
    if (modalMode === "certificate" && selectedApplicationId) {
      const payload = cleanPayload(form);
      const request =
        actionType === "generate"
          ? generateMemorialCertificate(selectedApplicationId, payload)
          : actionType === "approve"
            ? approveMemorialCertificate(selectedApplicationId, payload)
            : issueMemorialCertificate(selectedApplicationId, payload);
      return saveAndRefresh(request, "Certificate action completed", selectedApplicationId);
    }
    if (modalMode === "burial" && selectedApplicationId) {
      return saveAndRefresh(recordMemorialBurialVerification(selectedApplicationId, cleanPayload(form)), "Burial document verification recorded", selectedApplicationId);
    }
    if (modalMode === "action") return submitAction();
  };

  const submitAction = async () => {
    const payload = cleanPayload(form);
    if (actionType === "approveAppointment" && selectedAppointment?.id) {
      return saveAndRefresh(approveMemorialAppointment(selectedAppointment.id, payload), "Appointment approved");
    }
    if (actionType === "rejectAppointment" && selectedAppointment?.id) {
      return saveAndRefresh(rejectMemorialAppointment(selectedAppointment.id, payload), "Appointment rejected");
    }
    if (!selectedApplicationId) return;
    const actionMap: Record<string, () => Promise<any>> = {
      submitApplication: () => submitMemorialApplication(selectedApplicationId, payload),
      verifyApplication: () => verifyMemorialApplication(selectedApplicationId, payload),
      returnVerification: () => returnMemorialVerification(selectedApplicationId, payload),
      approveFinance: () => approveMemorialFinance(selectedApplicationId, payload),
      rejectFinance: () => rejectMemorialFinance(selectedApplicationId, payload),
      returnFinance: () => returnMemorialFinance(selectedApplicationId, payload),
      requestPaymentInfo: () => requestMemorialPaymentInfo(selectedApplicationId, payload),
      allocateAuto: () => allocateAvailableMemorialPlot(selectedApplicationId, payload.locationId, payload),
      allocateSelected: () => allocateMemorialPlot(selectedApplicationId, payload.plotId, payload),
    };
    if (!actionMap[actionType]) return;
    return saveAndRefresh(actionMap[actionType](), "Workflow updated", selectedApplicationId);
  };

  const openApplicationDetails = async (application: any) => {
    setLoading(true);
    const response = await getMemorialApplication(application.id);
    if (response?.status === 200) {
      setSelectedApplication(response.data);
      setTab("details");
    } else {
      showInfoToast(response?.data?.message || "Failed to load application");
    }
    setLoading(false);
  };

  const availablePercent = dashboard.totalPlots
    ? Math.round(((dashboard.availablePlots || 0) / dashboard.totalPlots) * 100)
    : 0;
  const metrics = [
    { label: "Total Plots", value: dashboard.totalPlots || 0, icon: <PlaceOutlinedIcon />, caption: "memorial plots on record" },
    { label: "Available", value: dashboard.availablePlots || 0, icon: <Inventory2OutlinedIcon />, caption: `${availablePercent}% availability` },
    { label: "Pending Apps", value: dashboard.pendingApplications || 0, icon: <AssignmentOutlinedIcon />, caption: "awaiting action" },
    { label: "Finance Queue", value: dashboard.pendingFinanceApprovals || 0, icon: <PaymentsOutlinedIcon />, caption: "awaiting review / payment" },
    { label: "Certificates", value: dashboard.certificatesIssued || 0, icon: <DescriptionOutlinedIcon />, caption: "issued to date" },
  ];

  return (
    <OperationsPageShell
      eyebrow=""
      title="Memorials Portal"
      description="Manage memorial enquiries, appointments, plot inventory, applications, payments, approvals, certificates, and burial document verification."
      meta={
        <>
          <Chip label={`${dashboard.pendingAppointments || 0} appointment requests`} size="small" sx={{ bgcolor: "#FFF4EB", color: ifasColors.orange, fontWeight: 800 }} />
          <Chip label={`${dashboard.allocatedPlots || 0} allocated plots`} size="small" sx={{ bgcolor: "#EAF7EF", color: ifasColors.success, fontWeight: 800 }} />
        </>
      }
      actions={
        <Box sx={{ ...operationsActionBarSx, flexWrap: "nowrap" }}>
          <Tooltip title="Refresh memorial workspace">
            <Button
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              onClick={loadAll}
              sx={{ bgcolor: "#FFFFFF", color: ifasColors.text, borderColor: ifasColors.border, whiteSpace: "nowrap", "&:hover": { bgcolor: "#FFF4EB", borderColor: ifasColors.orange } }}
            >
              Refresh
            </Button>
          </Tooltip>
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openModal("enquiry", emptyEnquiry)} sx={{ whiteSpace: "nowrap" }}>Enquiry</Button>
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openModal("application", emptyApplication)} sx={{ whiteSpace: "nowrap" }}>Application</Button>
        </Box>
      }
    >
      {loading ? <Loader isLoading={loading} /> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }, gap: 2, mb: 2.5 }}>
        {metrics.map((metric) => <OperationsMetricCard key={metric.label} {...metric} />)}
      </Box>

      <Paper elevation={0} sx={{ ...operationsDetailPaperSx, mb: 2, p: 0 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
          <Tab value="enquiries" label="Enquiries" />
          <Tab value="applications" label="Applications" />
          <Tab value="appointments" label="Appointments" />
          <Tab value="plots" label="Plots" />
          <Tab value="reports" label="Reports" />
          <Tab value="details" label="Details" disabled={!selectedApplication} />
        </Tabs>
      </Paper>

      {tab === "enquiries" && renderEnquiries()}
      {tab === "applications" && renderApplications()}
      {tab === "appointments" && renderAppointments()}
      {tab === "plots" && renderPlots()}
      {tab === "reports" && renderReports()}
      {tab === "details" && selectedApplication && renderDetails()}

      <Box sx={{ mt: 2.5 }}>{renderCommandCentre()}</Box>

      {renderDialog()}
    </OperationsPageShell>
  );

  function renderCommandCentre() {
    return (
      <Paper elevation={0} sx={{ ...operationsDetailPaperSx, p: { xs: 1.6, md: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mb={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 6, height: 18, borderRadius: 999, bgcolor: ifasColors.orange }} />
            <Typography fontWeight={900} color={ifasColors.text}>Memorial Lifecycle</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography fontSize={12.5} fontWeight={800} color={ifasColors.muted}>single operational workflow</Typography>
            <Button
              variant="text"
              onClick={() => setShowCoverage((value) => !value)}
              endIcon={<KeyboardArrowDownOutlinedIcon sx={{ transform: showCoverage ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />}
              sx={{ color: ifasColors.orange, fontWeight: 800, textTransform: "none", fontSize: 12.5, minWidth: 0, px: 0.5 }}
            >
              Workflow coverage
            </Button>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 1.5, border: `1px solid ${ifasColors.border}`, bgcolor: "#FAFBFC", mt: 0.5, overflowX: "auto" }}>
          <Box sx={{ display: "flex" }}>
            {([
              ["Draft", "done"],
              ["Appointment Requested", "done"],
              ["Appointment Confirmed", "done"],
              ["Consultation Completed", "done"],
              ["Pending Verification", "done"],
              ["Pending Finance Approval", "cur"],
              ["Pending Certificate Approval", "future"],
              ["Certificate Generated", "future"],
              ["Certificate Issued", "future"],
              ["Closed", "future"],
            ] as [string, "done" | "cur" | "future"][]).map(([label, state], index) => (
              <Box key={label} sx={{ flex: 1, minWidth: 100, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                {index > 0 && (
                  <Box sx={{ position: "absolute", top: 16, left: "calc(-50% + 16px)", right: "calc(50% + 16px)", height: 2, bgcolor: state === "future" ? ifasColors.border : ifasColors.success }} />
                )}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12.5,
                    fontWeight: 800,
                    ...(state === "done"
                      ? { bgcolor: ifasColors.success, color: "#fff" }
                      : state === "cur"
                        ? { bgcolor: ifasColors.orange, color: "#fff", boxShadow: "0 0 0 4px #FFF4EB" }
                        : { border: `2px solid ${ifasColors.border}`, color: ifasColors.muted, bgcolor: "#FFFFFF" }),
                  }}
                >
                  {state === "done" ? "✓" : index + 1}
                </Box>
                <Typography
                  sx={{
                    mt: 1.25,
                    fontSize: 10.5,
                    fontWeight: 700,
                    textAlign: "center",
                    maxWidth: 100,
                    lineHeight: 1.35,
                    color: state === "cur" ? ifasColors.orange : state === "done" ? ifasColors.text : ifasColors.muted,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {showCoverage && (
        <>
        <Typography sx={{ mt: 2, mb: 1, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: ifasColors.muted }}>
          Requirement coverage
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 0 }}>
          {requirementCards.map((item, index) => (
            <Box
              key={item.title}
              sx={{
                px: { xs: 0, md: 2 },
                py: 0.5,
                borderLeft: { md: index === 0 ? "none" : `1px solid ${ifasColors.border}` },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: ifasColors.success }} />
                <Typography fontSize={13.5} fontWeight={900} color={ifasColors.text}>{item.title}</Typography>
              </Box>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.5} lineHeight={1.55}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
        </>
        )}
      </Paper>
    );
  }

  function openAppointmentFromEnquiry(enquiry: any) {
    openModal("appointment", {
      ...emptyAppointment,
      enquiryId: enquiry.id,
      locationId: enquiry.preferredLocation?.id || "",
      appointmentType: enquiry.requestType || "CONSULTATION",
      appointmentDate: enquiry.preferredDate || "",
      appointmentTime: toTimeValue(enquiry.preferredTime) || "09:00",
      notes: enquiry.additionalNotes || "",
    });
  }

  function openApplicationFromEnquiry(enquiry: any) {
    openModal("application", {
      ...emptyApplication,
      enquiryId: enquiry.id,
      applicantName: enquiry.applicantName || "",
      applicantContactNumber: enquiry.contactNumber || "",
      applicantEmail: enquiry.emailAddress || "",
      branchLocation: enquiry.preferredLocation?.name || "",
      consultationNotes: enquiry.additionalNotes || "",
    });
  }

  function renderEnquiries() {
    const rows = filterRows(enquiries, q("enquiries"), (enquiry) => [
      enquiry.enquiryReference,
      enquiry.applicantName,
      enquiry.contactNumber,
      enquiry.preferredLocation?.name,
      enquiry.requestType,
      enquiry.status,
    ]);
    return (
      <MemorialTablePanel
        title="Memorial Enquiries"
        count={rows.length}
        searchValue={q("enquiries")}
        onSearch={setQ("enquiries")}
        actions={
          <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openModal("enquiry", emptyEnquiry)} sx={memorialPrimaryButtonSx}>
            Enquiry
          </Button>
        }
      >
        <TableContainer component={Paper} elevation={0} sx={memorialTableInnerSx}>
          <Table>
            <TableHead sx={memorialTableHeadSx}>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Interest</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((enquiry) => (
                <TableRow key={enquiry.id} hover sx={memorialRowSx} onClick={() => openApplicationFromEnquiry(enquiry)}>
                  <TableCell>
                    <Typography fontSize={14} fontWeight={900} color={ifasColors.text}>
                      {enquiry.enquiryReference}
                    </Typography>
                  </TableCell>
                  <TableCell>{display(enquiry.applicantName)}</TableCell>
                  <TableCell><ChannelChip value={enquiry.clientSource} /></TableCell>
                  <TableCell>{formatOperationsStatus(enquiry.requestType)}</TableCell>
                  <TableCell>{enquiry.preferredDate ? toDate(enquiry.preferredDate) : display(enquiry.preferredDate)}</TableCell>
                  <TableCell><StatusChip status={enquiry.status} /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <EmptyTableRow colSpan={6} label="No memorial enquiries captured yet." />}
            </TableBody>
          </Table>
        </TableContainer>
      </MemorialTablePanel>
    );
  }

  function renderApplications() {
    const rows = filterRows(applications, q("applications"), (application) => [
      application.applicationNumber,
      application.applicantName,
      application.deceasedName,
      application.selectedPlot?.plotNumber,
      application.status,
      application.financeStatus,
    ]);
    return (
      <MemorialTablePanel
        title="Memorial Applications"
        count={rows.length}
        searchValue={q("applications")}
        onSearch={setQ("applications")}
      >
        <TableContainer component={Paper} elevation={0} sx={memorialTableInnerSx}>
          <Table>
            <TableHead sx={memorialTableHeadSx}>
              <TableRow>
                <TableCell>Application</TableCell>
                <TableCell>Applicant</TableCell>
                <TableCell>Deceased</TableCell>
                <TableCell>Plot</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Finance</TableCell>
                <TableCell>Total</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((application) => (
                <TableRow key={application.id} hover sx={memorialRowSx} onClick={() => openApplicationDetails(application)}>
                  <TableCell>{application.applicationNumber}</TableCell>
                  <TableCell>{display(application.applicantName)}</TableCell>
                  <TableCell>{display(application.deceasedName)}</TableCell>
                  <TableCell>{display(application.selectedPlot?.plotNumber)}</TableCell>
                  <TableCell><StatusChip status={application.status} /></TableCell>
                  <TableCell><StatusChip status={application.financeStatus} /></TableCell>
                  <TableCell>{formatR(application.totalPrice)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open application">
                      <IconButton onClick={(event) => { event.stopPropagation(); openApplicationDetails(application); }}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <EmptyTableRow colSpan={8} label="No memorial applications found." />}
            </TableBody>
          </Table>
        </TableContainer>
      </MemorialTablePanel>
    );
  }

  function renderAppointments() {
    const rows = filterRows(appointments, q("appointments"), (appointment) => [
      appointment.bookingReference,
      appointment.location?.name,
      appointment.appointmentType,
      appointment.status,
    ]);
    return (
      <MemorialTablePanel
        title="Appointment Calendar"
        count={rows.length}
        searchValue={q("appointments")}
        onSearch={setQ("appointments")}
        actions={
          <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openModal("appointment", emptyAppointment)} sx={memorialPrimaryButtonSx}>
            Appointment
          </Button>
        }
      >
        <TableContainer component={Paper} elevation={0} sx={memorialTableInnerSx}>
          <Table>
            <TableHead sx={memorialTableHeadSx}>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((appointment) => (
                <TableRow key={appointment.id} sx={memorialRowSx}>
                  <TableCell>{appointment.bookingReference}</TableCell>
                  <TableCell>{display(appointment.location?.name)}</TableCell>
                  <TableCell>{formatOperationsStatus(appointment.appointmentType)}</TableCell>
                  <TableCell>{toDate(appointment.appointmentDate)}</TableCell>
                  <TableCell>{toTime(appointment.appointmentTime)}</TableCell>
                  <TableCell><StatusChip status={appointment.status} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Approve">
                      <IconButton onClick={() => { setSelectedAppointment(appointment); setActionType("approveAppointment"); openModal("action", emptyAction); }}>
                        <CheckCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton onClick={() => { setSelectedAppointment(appointment); setActionType("rejectAppointment"); openModal("action", emptyAction); }}>
                        <DescriptionOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <EmptyTableRow colSpan={7} label="No appointment requests found." />}
            </TableBody>
          </Table>
        </TableContainer>
      </MemorialTablePanel>
    );
  }

  function renderPlots() {
    const plotRows = filterRows(plots, q("plots"), (plot) => [
      plot.plotNumber,
      plot.location?.name,
      plot.status,
      plot.allocatedApplicationId,
    ]);
    return (
      <Box>
        <OperationsSectionHeader title="Site Viewing" meta="location and 360 viewing setup" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }, gap: 2, mb: 2 }}>
          {locations.map((location) => {
            const locationPlots = plots.filter((plot) => plot.location?.id === location.id);
            const openPlots = locationPlots.filter((plot) => plot.status === "AVAILABLE").length;
            return (
              <Paper
                key={location.id}
                elevation={0}
                sx={{
                  p: 1.75,
                  minHeight: 172,
                  borderRadius: 2,
                  border: `1px solid ${ifasColors.border}`,
                  background: "linear-gradient(135deg, #FFFFFF 0%, #F8FBFA 100%)",
                  boxShadow: "0 8px 20px rgba(20, 24, 31, 0.045)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box>
                      <Typography fontSize={16} fontWeight={900} color={ifasColors.text}>{location.name}</Typography>
                      <Typography fontSize={12.5} color={ifasColors.muted} mt={0.4} lineHeight={1.45}>
                        {location.description || "Memorial viewing and consultation location."}
                      </Typography>
                    </Box>
                    <Chip size="small" label={location.active ? "Active" : "Inactive"} sx={{ ...getOperationsStatusSx(location.active ? "AVAILABLE" : "UNAVAILABLE"), fontWeight: 900 }} />
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                    <Chip size="small" label={`${locationPlots.length} plots`} />
                    <Chip size="small" label={`${openPlots} available`} sx={{ bgcolor: "#EAF7EF", color: ifasColors.success, fontWeight: 800 }} />
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant={location.siteViewUrl ? "contained" : "outlined"}
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => openSiteView(location)}
                  sx={{
                    alignSelf: "flex-start",
                    mt: 1.5,
                    textTransform: "none",
                    fontWeight: 950,
                    borderRadius: "10px",
                    px: 1.6,
                    ...(location.siteViewUrl
                      ? {
                          bgcolor: ifasColors.orange,
                          color: "#FFFFFF",
                          boxShadow: "0 10px 18px rgba(234, 104, 0, 0.18)",
                          "&:hover": { bgcolor: "#C95A00" },
                        }
                      : {
                          borderColor: alpha(ifasColors.orange, 0.58),
                          bgcolor: alpha(ifasColors.orange, 0.08),
                          color: ifasColors.orange,
                          "&:hover": {
                            borderColor: ifasColors.orange,
                            bgcolor: alpha(ifasColors.orange, 0.14),
                          },
                        }),
                  }}
                >
                  {location.siteViewUrl ? "3D View" : "Add 3D View"}
                </Button>
              </Paper>
            );
          })}
          {locations.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Configure Manhill, Johannesburg, or another memorial location to unlock site viewing and plot management.
            </Alert>
          )}
        </Box>

        <MemorialTablePanel
          title="Plot Inventory"
          count={plotRows.length}
          searchValue={q("plots")}
          onSearch={setQ("plots")}
          actions={
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button size="small" variant="outlined" startIcon={<AddOutlinedIcon />} onClick={() => openModal("location", emptyLocation)} sx={memorialOutlineButtonSx}>Location</Button>
              <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => openModal("plot", emptyPlot)} sx={memorialPrimaryButtonSx}>Plot</Button>
              <Button size="small" variant="outlined" startIcon={<AddOutlinedIcon />} onClick={() => openModal("timeSlot", emptyTimeSlot)} sx={memorialOutlineButtonSx}>Time Slot</Button>
            </Box>
          }
        >
        <TableContainer component={Paper} elevation={0} sx={memorialTableInnerSx}>
          <Table>
            <TableHead sx={memorialTableHeadSx}>
              <TableRow>
                <TableCell>Plot Number</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Maintenance</TableCell>
                <TableCell>Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plotRows.map((plot) => (
                <TableRow key={plot.id} sx={memorialRowSx}>
                  <TableCell>{plot.plotNumber}</TableCell>
                  <TableCell>{display(plot.location?.name)}</TableCell>
                  <TableCell><StatusChip status={plot.status} /></TableCell>
                  <TableCell>{formatR(plot.price)}</TableCell>
                  <TableCell>{formatR(plot.maintenanceFee)} / {plot.maintenanceTermYears || 10} years</TableCell>
                  <TableCell>{display(plot.allocatedApplicationId)}</TableCell>
                </TableRow>
              ))}
              {plotRows.length === 0 && <EmptyTableRow colSpan={6} label="No memorial plots configured." />}
            </TableBody>
          </Table>
        </TableContainer>
        </MemorialTablePanel>
      </Box>
    );
  }

  function renderReports() {
    const groups = [
      ["Appointment Reports", reports.appointmentReports, "Bookings, upcoming work, cancellations, and walk-in versus online mix."],
      ["Application Reports", reports.applicationReports, "Submitted, approved, rejected, and workflow queue visibility."],
      ["Certificate Reports", reports.certificateReports, "Generated, issued, and pending GCCO certificate approval."],
      ["Plot Management Reports", reports.plotManagementReports, "Total, allocated, available, and location-level inventory signals."],
      ["Financial Reports", reports.financialReports, "Revenue, outstanding payments, payment history, and maintenance fee tracking."],
    ];
    return (
      <Box display="grid" gap={2}>
        <Paper
          elevation={0}
          sx={{
            ...operationsDetailPaperSx,
            p: { xs: 1.5, md: 2 },
            background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
          }}
        >
          <OperationsSectionHeader
            title="Report Filters"
            meta={
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={resetReports} disabled={saving}>Reset</Button>
                <Button size="small" variant="contained" startIcon={<SearchOutlinedIcon />} onClick={() => loadReports()} disabled={saving}>
                  Apply Filters
                </Button>
              </Box>
            }
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 1.25 }}>
            <ReportFilterField name="fromDate" label="From Date" type="date" />
            <ReportFilterField name="toDate" label="To Date" type="date" />
            <ReportFilterField name="locationId" label="Memorial Location" select options={[{ value: "", label: "All Locations" }, ...locationOptions()]} />
            <ReportFilterField name="branchLocation" label="Branch / Location" />
            <ReportFilterField name="applicationStatus" label="Application Status" select options={[{ value: "", label: "All Application Statuses" }, ...enumOptions(applicationStatusOptions)]} />
            <ReportFilterField name="financeStatus" label="Finance Status" select options={[{ value: "", label: "All Finance Statuses" }, ...enumOptions(financeStatusOptions)]} />
            <ReportFilterField name="certificateStatus" label="Certificate Status" select options={[{ value: "", label: "All Certificate Statuses" }, ...enumOptions(certificateStatusOptions)]} />
            <ReportFilterField name="plotStatus" label="Plot Status" select options={[{ value: "", label: "All Plot Statuses" }, ...enumOptions(plotStatusOptions)]} />
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
          {groups.map(([title, values, description]: any) => (
            <ReportCard key={title} title={title} description={description} values={values || {}} />
          ))}
        </Box>
      </Box>
    );
  }

  function ReportFilterField({ name, label, type = "text", select = false, options = [] }: any) {
    const value = reportFilters[name] ?? "";
    const sx = {
      "& .MuiOutlinedInput-root": {
        borderRadius: 1.5,
        bgcolor: "#FFFFFF",
      },
      "& .MuiInputLabel-root": {
        fontSize: 13,
        color: ifasColors.muted,
      },
    };
    if (select) {
      return (
        <FormControl fullWidth size="small" sx={sx}>
          <InputLabel>{label}</InputLabel>
          <Select label={label} value={value} onChange={(event) => setReportFilters((prev: any) => ({ ...prev, [name]: event.target.value }))}>
            {options.map((option: any) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        type={type}
        value={value}
        onChange={(event) => setReportFilters((prev: any) => ({ ...prev, [name]: event.target.value }))}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
        sx={sx}
      />
    );
  }

  function renderDetails() {
    const application = selectedApplication.application || {};
    const documents = selectedApplication.documents || [];
    const payments = selectedApplication.payments || [];
    const certificate = selectedApplication.certificate;
    return (
      <Box display="grid" gap={2}>
        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5} flexWrap="wrap">
            <Box>
              <Typography fontWeight={900} color={ifasColors.text}>{application.applicationNumber}</Typography>
              <Typography fontSize={13.5} color={ifasColors.muted}>{display(application.applicantName)} - {display(application.deceasedName)}</Typography>
            </Box>
            <Box sx={operationsActionBarSx}>
              <Button variant="outlined" onClick={() => openWorkflowAction("submitApplication")}>Submit</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("allocateAuto")}>Auto Allocate</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("allocateSelected")}>Select Plot</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("verifyApplication")}>Verify</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("returnVerification")}>Return Verification</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("approveFinance")}>Finance OK</Button>
              <Button variant="outlined" onClick={() => openModal("document", emptyDocument)}>Document</Button>
              <Button variant="outlined" onClick={() => { setActionType("invoice"); openModal("payment", { ...emptyPayment, amount: application.totalPrice || "" }); }}>Invoice</Button>
              <Button variant="contained" onClick={() => { setActionType("generate"); openModal("certificate", emptyAction); }}>Certificate</Button>
            </Box>
          </Box>
          <Box mt={2}>
            <WorkflowRail status={application.status} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.25, mt: 2 }}>
            <GateCard title="Verification" status={application.verificationStatus} text={application.verificationComments || "Application completeness, documents, applicant, deceased, plot, and pricing."} />
            <GateCard title="Finance" status={application.financeStatus} text={application.financeComments || "Payment confirmation and financial clearance before certificate generation."} />
            <GateCard title="Certificate" status={certificate?.status || "NOT_STARTED"} text={certificate?.certificateNumber || "Ownership certificate pending approval and issue."} />
            <GateCard title="Burial Originals" status={application.originalDocumentsVerified ? "COMPLETED" : "PENDING"} text={application.burialDocumentsVerifiedBy || "Original Burial Order, Deceased ID, and Applicant ID required."} />
          </Box>
          <OperationsDetailGrid>
            <OperationsDetailField label="Application Status" value={<StatusChip status={application.status} />} />
            <OperationsDetailField label="Finance Status" value={<StatusChip status={application.financeStatus} />} />
            <OperationsDetailField label="Verification" value={<StatusChip status={application.verificationStatus} />} />
            <OperationsDetailField label="Payment" value={<StatusChip status={application.paymentStatus} />} />
            <OperationsDetailField label="Applicant ID" value={application.applicantIdNumber} />
            <OperationsDetailField label="Applicant Contact" value={application.applicantContactNumber} />
            <OperationsDetailField label="Applicant Email" value={application.applicantEmail} />
            <OperationsDetailField label="Selected Plot" value={application.selectedPlot?.plotNumber} />
            <OperationsDetailField label="Location" value={application.selectedPlot?.location?.name || application.branchLocation} />
            <OperationsDetailField label="Package" value={application.memorialPackage} />
            <OperationsDetailField label="Total Price" value={formatR(application.totalPrice)} />
            <OperationsDetailField label="Original Docs Verified" value={application.originalDocumentsVerified ? "Yes" : "No"} />
            <OperationsDetailField label="Consultation Notes" value={application.consultationNotes} span={3} />
          </OperationsDetailGrid>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
          <Paper elevation={0} sx={operationsDetailPaperSx}>
            <OperationsSectionHeader title="Documents" meta={`${documents.length} linked`} />
            {documents.map((document: any) => (
              <DetailLine key={document.id} left={formatOperationsStatus(document.documentType)} right={document.certifiedCopy ? "Certified" : "Not certified"} />
            ))}
            {documents.length === 0 && <Typography color={ifasColors.muted}>No documents linked.</Typography>}
          </Paper>
          <Paper elevation={0} sx={operationsDetailPaperSx}>
            <OperationsSectionHeader title="Payments" meta={`${payments.length} records`} />
            {payments.map((payment: any) => (
              <DetailLine key={payment.id} left={`${payment.invoiceNumber} - ${formatR(payment.amount)}`} right={formatOperationsStatus(payment.status)} />
            ))}
            <Box sx={operationsActionBarSx} mt={1.5}>
              <Button variant="outlined" startIcon={<PaymentsOutlinedIcon />} onClick={() => { setActionType("pop"); openModal("payment", emptyPayment); }}>POP</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("requestPaymentInfo")}>Request Info</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("returnFinance")}>Return Finance</Button>
              <Button variant="outlined" onClick={() => openWorkflowAction("rejectFinance")}>Reject Finance</Button>
            </Box>
          </Paper>
          <Paper elevation={0} sx={operationsDetailPaperSx}>
            <OperationsSectionHeader title="Certificate" meta={certificate ? formatOperationsStatus(certificate.status) : "Not generated"} />
            <OperationsDetailGrid>
              <OperationsDetailField label="Certificate Number" value={certificate?.certificateNumber} />
              <OperationsDetailField label="GCCO Signed By" value={certificate?.gccoSignedBy} />
              <OperationsDetailField label="Issue Date" value={toDate(certificate?.issueDate)} />
            </OperationsDetailGrid>
            <Box sx={operationsActionBarSx} mt={1.5}>
              <Button variant="outlined" onClick={() => { setActionType("approve"); openModal("certificate", emptyAction); }}>Approve</Button>
              <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={() => { setActionType("issue"); openModal("certificate", emptyAction); }}>Issue</Button>
            </Box>
          </Paper>
          <Paper elevation={0} sx={operationsDetailPaperSx}>
            <OperationsSectionHeader title="Burial Requirements" meta={application.originalDocumentsVerified ? "Verified" : "Pending"} />
            <Typography fontSize={13.5} color={ifasColors.muted}>
              Original Burial Order, Deceased ID, and Applicant ID must be verified before burial.
            </Typography>
            <Box sx={operationsActionBarSx} mt={1.5}>
              <Button variant="contained" onClick={() => openModal("burial", emptyBurial)}>Record Verification</Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  function openWorkflowAction(type: string) {
    setActionType(type);
    const base = type === "allocateAuto" ? { ...emptyAction, locationId: locations[0]?.id || "" } : emptyAction;
    const selectedPlot = type === "allocateSelected" ? { ...base, plotId: availablePlots[0]?.id || "" } : base;
    openModal("action", selectedPlot);
  }

  function openSiteView(location: any) {
    if (location?.siteViewUrl) {
      window.open(location.siteViewUrl, "_blank", "noopener,noreferrer");
      return;
    }
    showInfoToast("Add a 3D / 360 view URL for this location first");
    openModal("location", {
      name: location?.name || "",
      description: location?.description || "",
      siteViewUrl: "",
      active: location?.active ?? true,
    });
  }

  function renderDialog() {
    const titleMap: Record<string, string> = {
      enquiry: "Capture Memorial Enquiry",
      appointment: "Request Appointment",
      application: "Capture Memorial Application",
      location: "Configure Memorial Location",
      plot: "Create Memorial Plot",
      timeSlot: "Configure Appointment Time Slot",
      document: "Attach Supporting Document",
      payment: actionType === "invoice" ? "Generate Invoice" : "Upload Proof of Payment",
      certificate: "Certificate Action",
      burial: "Burial Document Verification",
      action: "Workflow Action",
    };
    return (
      <Dialog
        open={Boolean(modalMode)}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: {
            backdropFilter: "blur(9px)",
            backgroundColor: "rgba(15, 23, 42, 0.48)",
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: "min(920px, calc(100vw - 32px))",
            maxHeight: "min(84vh, 760px)",
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha("#FFFFFF", 0.72)}`,
            boxShadow: "0 26px 70px rgba(15, 23, 42, 0.34)",
            bgcolor: "#F8FAFC",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            borderBottom: `1px solid ${ifasColors.border}`,
            background: "linear-gradient(135deg, #FFFFFF 0%, #F9FBFD 62%, #FFF4EB 100%)",
          }}
        >
          <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2.2, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Box display="flex" gap={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(ifasColors.orange, 0.12),
                  color: ifasColors.orange,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AssignmentOutlinedIcon />
              </Box>
              <Box>
                <Typography fontSize={{ xs: 18, md: 21 }} fontWeight={950} color={ifasColors.text} lineHeight={1.2}>
                  {titleMap[modalMode] || "Memorial Action"}
                </Typography>
                <Typography fontSize={13.5} color={ifasColors.muted} mt={0.45} lineHeight={1.45}>
                  Capture confirmed information only. Audit trail, notifications, and dashboard metrics update after save.
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Close">
              <IconButton
                onClick={closeModal}
                size="small"
                sx={{
                  border: `1px solid ${ifasColors.border}`,
                  bgcolor: "#FFFFFF",
                  color: ifasColors.muted,
                  "&:hover": { bgcolor: "#FFF4EB", color: ifasColors.orange },
                }}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 2.75 }, bgcolor: "#F8FAFC" }}>
          {modalMode === "document" && (
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
              Uploaded copies must be certified. Original documents are still required before burial.
            </Alert>
          )}
          {modalMode === "certificate" && (
            <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
              Certificate actions are allowed only after verification, payment confirmation, and finance approval.
            </Alert>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr)" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <DialogContextPanel mode={modalMode} actionType={actionType} />
            <Box sx={{ display: "grid", gap: 1.5, alignContent: "start" }}>
              {modalMode === "enquiry" && EnquiryFields()}
              {modalMode === "appointment" && AppointmentFields()}
              {modalMode === "application" && ApplicationFields()}
              {modalMode === "location" && LocationFields()}
              {modalMode === "plot" && PlotFields()}
              {modalMode === "timeSlot" && TimeSlotFields()}
              {modalMode === "document" && DocumentFields()}
              {modalMode === "payment" && PaymentFields()}
              {modalMode === "certificate" && CertificateFields()}
              {modalMode === "burial" && BurialFields()}
              {modalMode === "action" && ActionFields()}
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            px: { xs: 2, md: 2.75 },
            py: 1.5,
            bgcolor: "#FFFFFF",
            gap: 1,
            borderTop: `1px solid ${ifasColors.border}`,
          }}
        >
          <Button
            onClick={closeModal}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 900, color: ifasColors.muted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitModal}
            disabled={saving}
            sx={{
              minWidth: 118,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 950,
              boxShadow: "0 12px 26px rgba(234, 104, 0, 0.28)",
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function Field({ name, label, type = "text", multiline = false, select = false, options = [] }: any) {
    const fieldSx = {
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        bgcolor: "#FFFFFF",
        minHeight: multiline ? undefined : 44,
        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
        "& fieldset": {
          borderColor: alpha("#64748B", 0.26),
        },
        "&:hover fieldset": {
          borderColor: alpha(ifasColors.orange, 0.42),
        },
        "&.Mui-focused fieldset": {
          borderColor: ifasColors.orange,
          borderWidth: 1.5,
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: 13,
        color: ifasColors.muted,
      },
      "& .MuiInputBase-input": {
        fontSize: 13.5,
        fontWeight: 700,
        color: ifasColors.text,
      },
    };
    if (select) {
      return (
        <FormControl fullWidth size="small" sx={fieldSx}>
          <InputLabel>{label}</InputLabel>
          <Select label={label} value={form[name] ?? ""} onChange={(event) => setForm((prev: any) => ({ ...prev, [name]: event.target.value }))}>
            {options.map((option: any) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        type={type}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        value={form[name] ?? ""}
        onChange={(event) => setForm((prev: any) => ({ ...prev, [name]: event.target.value }))}
        InputLabelProps={["date", "time"].includes(type) ? { shrink: true } : undefined}
        sx={fieldSx}
      />
    );
  }

  function EnquiryFields() {
    return (
      <FormSection title="Client and Request">
        {Field({ name: "clientSource", label: "Client Source", select: true, options: enumOptions(["ONLINE", "WALK_IN"]) })}
        {Field({ name: "requestType", label: "Request Type", select: true, options: enumOptions(["CONSULTATION", "PLOT_VIEWING"]) })}
        {Field({ name: "applicantName", label: "Applicant Name" })}
        {Field({ name: "contactNumber", label: "Contact Number" })}
        {Field({ name: "emailAddress", label: "Email Address" })}
        {Field({ name: "preferredLocationId", label: "Preferred Location", select: true, options: locationOptions() })}
        {Field({ name: "preferredDate", label: "Preferred Date", type: "date" })}
        {Field({ name: "preferredTime", label: "Preferred Time", type: "time" })}
        {Field({ name: "deceasedInformation", label: "Deceased Information", multiline: true })}
        {Field({ name: "additionalNotes", label: "Additional Notes", multiline: true })}
      </FormSection>
    );
  }

  function AppointmentFields() {
    return (
      <FormSection title="Booking Details">
        {Field({ name: "locationId", label: "Location", select: true, options: locationOptions() })}
        {Field({ name: "appointmentType", label: "Appointment Type", select: true, options: enumOptions(["CONSULTATION", "PLOT_VIEWING"]) })}
        {Field({ name: "appointmentDate", label: "Appointment Date", type: "date" })}
        {Field({ name: "appointmentTime", label: "Appointment Time", type: "time" })}
        {Field({ name: "enquiryId", label: "Enquiry Reference", select: true, options: enquiryOptions() })}
        {Field({ name: "bookedBy", label: "Booked By" })}
        {Field({ name: "notes", label: "Notes", multiline: true })}
      </FormSection>
    );
  }

  function ApplicationFields() {
    return (
      <>
      <FormSection title="Applicant and Deceased">
        {Field({ name: "enquiryId", label: "Enquiry Reference", select: true, options: enquiryOptions() })}
        {Field({ name: "applicantName", label: "Applicant Name" })}
        {Field({ name: "applicantContactNumber", label: "Contact Number" })}
        {Field({ name: "applicantEmail", label: "Email Address" })}
        {Field({ name: "applicantIdNumber", label: "Applicant ID" })}
        {Field({ name: "deceasedName", label: "Deceased Name" })}
        {Field({ name: "deceasedIdNumber", label: "Deceased ID" })}
        {Field({ name: "deceasedDateOfDeath", label: "Date of Death", type: "date" })}
      </FormSection>
      <FormSection title="Memorial Package and Consent">
        {Field({ name: "selectedPlotId", label: "Selected Plot", select: true, options: [{ value: "", label: "Allocate later" }, ...availablePlots.map((plot) => ({ value: plot.id, label: `${plot.plotNumber} - ${plot.location?.name}` }))] })}
        {Field({ name: "memorialPackage", label: "Memorial Package" })}
        {Field({ name: "branchLocation", label: "Branch / Location" })}
        {Field({ name: "consultationNotes", label: "Consultation Notes", multiline: true })}
      </FormSection>
      </>
    );
  }

  function LocationFields() {
    return (
      <FormSection title="Location Setup">
        {Field({ name: "name", label: "Location Name" })}
        {Field({ name: "siteViewUrl", label: "360 View URL" })}
        {Field({ name: "description", label: "Description", multiline: true })}
      </FormSection>
    );
  }

  function PlotFields() {
    return (
      <FormSection title="Inventory Pricing">
        {Field({ name: "locationId", label: "Location", select: true, options: locationOptions() })}
        {Field({ name: "plotNumber", label: "Plot Number" })}
        {Field({ name: "price", label: "Price", type: "number" })}
        {Field({ name: "maintenanceFee", label: "Maintenance Fee", type: "number" })}
        {Field({ name: "maintenanceTermYears", label: "Maintenance Term Years", type: "number" })}
      </FormSection>
    );
  }

  function TimeSlotFields() {
    return (
      <FormSection title="Appointment Availability">
        {Field({ name: "locationId", label: "Location", select: true, options: locationOptions() })}
        {Field({ name: "dayOfWeek", label: "Day Of Week", select: true, options: enumOptions(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]) })}
        {Field({ name: "slotTime", label: "Slot Time", type: "time" })}
      </FormSection>
    );
  }

  function DocumentFields() {
    return (
      <FormSection title="Certified Supporting Document">
        {Field({ name: "documentType", label: "Document Type", select: true, options: enumOptions(["APPLICANT_ID", "DECEASED_ID", "BURIAL_ORDER", "PROOF_OF_RELATIONSHIP", "PROOF_OF_PAYMENT", "ADDITIONAL_MEMORIAL_DOCUMENT"]) })}
        {Field({ name: "fileName", label: "File Name" })}
        {Field({ name: "fileUrl", label: "File URL" })}
        {Field({ name: "uploadedBy", label: "Uploaded By" })}
      </FormSection>
    );
  }

  function PaymentFields() {
    return (
      <FormSection title={actionType === "invoice" ? "Invoice Details" : "Proof of Payment"}>
        {Field({ name: "amount", label: "Amount", type: "number" })}
        {Field({ name: "paymentReference", label: "Payment Reference" })}
        {actionType !== "invoice" && Field({ name: "proofOfPaymentUrl", label: "Proof of Payment URL" })}
        {Field({ name: "actionedBy", label: "Actioned By" })}
        {Field({ name: "comments", label: "Comments", multiline: true })}
      </FormSection>
    );
  }

  function CertificateFields() {
    return (
      <FormSection title="Ownership Certificate">
        {Field({ name: "certificateUrl", label: "Certificate URL" })}
        {Field({ name: "signatureTemplateUrl", label: "Signature Template URL" })}
        {Field({ name: "gccoSignedBy", label: "GCCO Signed By" })}
        {Field({ name: "deliveryMethod", label: "Delivery Method", select: true, options: enumOptions(["CLIENT_PORTAL_DOWNLOAD", "EMAIL", "PRINTED_COPY", "COLLECTION"]) })}
        {Field({ name: "actionedBy", label: "Actioned By" })}
        {Field({ name: "comments", label: "Comments", multiline: true })}
      </FormSection>
    );
  }

  function BurialFields() {
    return (
      <FormSection title="Original Documents">
        {Field({ name: "verifiedBy", label: "Verified By" })}
        {Field({ name: "comments", label: "Comments", multiline: true })}
      </FormSection>
    );
  }

  function ActionFields() {
    return (
      <FormSection title="Action Decision">
        {actionType === "allocateAuto" && Field({ name: "locationId", label: "Location", select: true, options: locationOptions() })}
        {actionType === "allocateSelected" && Field({ name: "plotId", label: "Plot", select: true, options: availablePlots.map((plot) => ({ value: plot.id, label: `${plot.plotNumber} - ${plot.location?.name}` })) })}
        {Field({ name: "actionedBy", label: "Actioned By" })}
        {Field({ name: "comments", label: "Comments", multiline: true })}
        {actionType === "rejectAppointment" && Field({ name: "alternativeDate", label: "Alternative Date", type: "date" })}
        {actionType === "rejectAppointment" && Field({ name: "alternativeTime", label: "Alternative Time", type: "time" })}
      </FormSection>
    );
  }

  function locationOptions() {
    return locations.map((location) => ({ value: location.id, label: location.name }));
  }

  function enquiryOptions() {
    return [
      { value: "", label: "No linked enquiry" },
      ...enquiries.map((enquiry) => ({
        value: enquiry.id,
        label: `${enquiry.enquiryReference} - ${enquiry.applicantName || "Applicant"}`,
      })),
    ];
  }
}

function StatusChip({ status }: { status?: string }) {
  const statusSx = getOperationsStatusSx(status);
  return (
    <Chip
      size="small"
      label={
        <Box component="span" display="inline-flex" alignItems="center" gap={0.6}>
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: 999,
              bgcolor: "currentColor",
              flexShrink: 0,
            }}
          />
          {formatOperationsStatus(status)}
        </Box>
      }
      sx={{ ...statusSx, fontWeight: 900, borderRadius: 1.25 }}
    />
  );
}

function ChannelChip({ value }: { value?: string }) {
  const isOnline = String(value || "").toUpperCase().includes("ONLINE");
  const channelSx = isOnline
    ? { bgcolor: "#EAF1FF", color: "#1B56EB" }
    : { bgcolor: "#EEEEEE", color: "#616161" };
  return (
    <Chip
      size="small"
      label={
        <Box component="span" display="inline-flex" alignItems="center" gap={0.6}>
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: "currentColor", flexShrink: 0 }}
          />
          {formatOperationsStatus(value)}
        </Box>
      }
      sx={{ ...channelSx, fontWeight: 900, borderRadius: 1.25 }}
    />
  );
}

function WorkflowRail({ status }: { status?: string }) {
  const activeIndex = Math.max(
    workflowSteps.findIndex((step) => step === status),
    0,
  );
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${workflowSteps.length}, minmax(0, 1fr))` }, gap: 0.8 }}>
        {workflowSteps.map((step, index) => {
          const isDone = index < activeIndex;
          const isActive = index === activeIndex;
          return (
            <Box
              key={step}
              sx={{
                p: 1,
                minHeight: 58,
                borderRadius: 1.5,
                border: `1px solid ${isActive ? ifasColors.orange : ifasColors.border}`,
                bgcolor: isActive ? ifasColors.softOrange : isDone ? "#EAF7EF" : "#FFFFFF",
                color: isActive ? ifasColors.orange : isDone ? ifasColors.success : ifasColors.muted,
              }}
            >
              <Typography fontSize={10.5} fontWeight={900} lineHeight={1.25}>
                {formatOperationsStatus(step)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function SignalCard({
  title,
  value,
  caption,
  tone,
}: {
  title: string;
  value: string | number;
  caption: string;
  tone: "success" | "warning" | "info";
}) {
  const color = tone === "success" ? ifasColors.success : tone === "warning" ? ifasColors.orange : ifasColors.info;
  const progress = typeof value === "string" && value.endsWith("%") ? Number(value.replace("%", "")) : undefined;
  return (
    <Paper elevation={0} sx={{ p: 1.35, borderRadius: 1.5, border: `1px solid ${ifasColors.border}`, bgcolor: "#FFFFFF" }}>
      <Typography fontSize={12} fontWeight={900} color={ifasColors.muted} textTransform="uppercase">{title}</Typography>
      <Typography fontSize={26} fontWeight={900} color={ifasColors.text} lineHeight={1.1} mt={0.5}>{value}</Typography>
      {progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ my: 0.9, height: 7, borderRadius: 999, bgcolor: alpha(color, 0.12), "& .MuiLinearProgress-bar": { bgcolor: color } }}
        />
      )}
      <Typography fontSize={12.5} color={ifasColors.muted} lineHeight={1.4}>{caption}</Typography>
    </Paper>
  );
}

function GateCard({ title, status, text }: { title: string; status?: string; text?: string }) {
  return (
    <Paper elevation={0} sx={{ p: 1.35, borderRadius: 1.5, border: `1px solid ${ifasColors.border}`, bgcolor: "#FFFFFF" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography fontSize={13} fontWeight={900} color={ifasColors.text}>{title}</Typography>
        <StatusChip status={status} />
      </Box>
      <Typography fontSize={12.5} color={ifasColors.muted} lineHeight={1.45} mt={0.85}>
        {text || "No comments captured."}
      </Typography>
    </Paper>
  );
}

function ReportCard({ title, description, values }: { title: string; description: string; values: Record<string, any> }) {
  const entries = Object.entries(values || {});
  const total = entries.reduce((sum, [, value]) => sum + Number(value || 0), 0);
  return (
    <Paper
      elevation={0}
      sx={{
        ...operationsDetailPaperSx,
        p: 0,
        overflow: "hidden",
        bgcolor: "#FFFFFF",
      }}
    >
      <Box sx={{ p: 1.75, borderBottom: `1px solid ${ifasColors.border}`, bgcolor: "#FAFBFC" }}>
        <Box display="flex" justifyContent="space-between" gap={1.5} alignItems="flex-start">
          <Box>
            <Typography fontSize={15} fontWeight={900} color={ifasColors.text}>{title}</Typography>
            <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} lineHeight={1.45}>{description}</Typography>
          </Box>
          <Chip size="small" label={`${total} total`} sx={{ bgcolor: alpha(ifasColors.orange, 0.1), color: ifasColors.orange, fontWeight: 900 }} />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gap: 0 }}>
        {entries.map(([key, value]) => {
          const numeric = Number(value || 0);
          const percent = total > 0 ? Math.min(100, Math.round((numeric / total) * 100)) : 0;
          return (
            <Box key={key} sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${ifasColors.border}` }}>
              <Box display="flex" justifyContent="space-between" gap={1.5} alignItems="center">
                <Typography fontSize={13.5} fontWeight={850} color={ifasColors.text}>{formatOperationsStatus(key)}</Typography>
                <Typography fontSize={14} fontWeight={900} color={ifasColors.text}>{String(value)}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  mt: 0.85,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: alpha(ifasColors.info, 0.1),
                  "& .MuiLinearProgress-bar": { bgcolor: percent > 0 ? ifasColors.info : alpha(ifasColors.info, 0.2) },
                }}
              />
            </Box>
          );
        })}
        {entries.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography fontSize={13.5} color={ifasColors.muted}>No report data for the selected filters.</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.35, md: 1.65 },
        borderRadius: 2,
        border: `1px solid ${alpha("#CBD5E1", 0.9)}`,
        bgcolor: "#FFFFFF",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.045)",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.25}>
        <Box sx={{ width: 4, height: 18, borderRadius: 99, bgcolor: ifasColors.orange }} />
        <Typography fontSize={13.5} fontWeight={950} color={ifasColors.text}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
        {children}
      </Box>
    </Paper>
  );
}

function DialogContextPanel({ mode, actionType }: { mode: string; actionType: string }) {
  const context: Record<string, { title: string; lines: string[] }> = {
    enquiry: {
      title: "Intake checklist",
      lines: ["Capture source as Online or Walk-in.", "Preferred location and time feed appointment planning.", "Notes stay linked to the enquiry reference."],
    },
    appointment: {
      title: "Booking rules",
      lines: ["Use configured slots only.", "Confirmed and requested slots are blocked.", "Manager can approve, reject, or suggest alternatives."],
    },
    application: {
      title: "Application pack",
      lines: ["Applicant and deceased details are required.", "Plot can be selected now or allocated later.", "Declaration and consent are set by default."],
    },
    location: {
      title: "Site setup",
      lines: ["Manhill and Johannesburg are seeded.", "Add future locations without code changes.", "360 URL enables viewing access."],
    },
    plot: {
      title: "Plot inventory",
      lines: ["Default price is R30,000.", "Default maintenance is R25,000 for 10 years.", "Allocated plots cannot be reused."],
    },
    timeSlot: {
      title: "Calendar setup",
      lines: ["Configure location-specific appointment slots.", "Availability excludes booked or pending slots.", "Defaults are used when no custom slots exist."],
    },
    document: {
      title: "Document rules",
      lines: ["Applicant ID and Deceased ID are mandatory for verification.", "Every uploaded copy must be certified.", "Originals are checked on burial day."],
    },
    payment: {
      title: actionType === "invoice" ? "Invoice gate" : "Finance routing",
      lines: ["Invoice creates a payment reference.", "POP upload moves work to Finance.", "Finance can approve, reject, return, or request info."],
    },
    certificate: {
      title: "Ownership certificate",
      lines: ["Requires verification and finance approval.", "GCCO approval happens before issue.", "Issue channel and download or collection status are recorded."],
    },
    burial: {
      title: "Burial day",
      lines: ["Confirm original Burial Order.", "Confirm original Deceased ID.", "Confirm original Applicant ID."],
    },
    action: {
      title: "Workflow decision",
      lines: ["Comments are stored in the audit trail.", "Status and dashboard figures update after save.", "Use return actions when correction is required."],
    },
  };
  const selected = context[mode] || context.action;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.65,
        borderRadius: 2.4,
        border: `1px solid ${alpha(ifasColors.orange, 0.22)}`,
        background: "linear-gradient(180deg, #FFFFFF 0%, #FFF9F4 100%)",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
        alignSelf: "start",
        position: { md: "sticky" },
        top: { md: 0 },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          bgcolor: alpha(ifasColors.orange, 0.14),
          color: ifasColors.orange,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.2,
        }}
      >
        <AssignmentOutlinedIcon />
      </Box>
      <Typography fontSize={15} fontWeight={900} color={ifasColors.text}>
        {selected.title}
      </Typography>
      <Typography fontSize={12.5} color={ifasColors.muted} mt={0.45} lineHeight={1.45}>
        These checks keep the Memorials workflow consistent for Admin, Finance, and GCCO approval.
      </Typography>
      <Stack spacing={1} mt={1.25}>
        {selected.lines.map((line) => (
          <Box
            key={line}
            display="flex"
            gap={0.85}
            alignItems="flex-start"
            sx={{ p: 0.9, borderRadius: 1.5, bgcolor: alpha("#FFFFFF", 0.72), border: `1px solid ${alpha("#CBD5E1", 0.55)}` }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: ifasColors.success, mt: 0.15, flexShrink: 0 }} />
            <Typography fontSize={12.5} color={ifasColors.muted} lineHeight={1.45}>
              {line}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function EmptyTableRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Box py={4} textAlign="center" color={ifasColors.muted}>{label}</Box>
      </TableCell>
    </TableRow>
  );
}

function DetailLine({ left, right }: { left: string; right: string }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={1.5} py={1} borderBottom={`1px solid ${ifasColors.border}`}>
      <Typography fontSize={13.5} fontWeight={800} color={ifasColors.text}>{left}</Typography>
      <Typography fontSize={13.5} color={ifasColors.muted} textAlign="right">{right}</Typography>
    </Box>
  );
}

function enumOptions(values: string[]) {
  return values.map((value) => ({ value, label: formatOperationsStatus(value) }));
}

function cleanPayload(payload: Record<string, any>) {
  return Object.entries(payload).reduce((acc: Record<string, any>, [key, value]) => {
    if (value === "") return acc;
    if (["price", "maintenanceFee", "amount", "maintenanceTermYears"].includes(key)) {
      acc[key] = Number(value);
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
}
